import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  BarChart3,
} from "lucide-react";

export default function Universities() {
  const features = [
    {
      icon: Users,
      title: "Bulk Student Accounts",
      description: "Create and manage accounts for thousands of students at once with our enterprise management portal.",
    },
    {
      icon: BarChart3,
      title: "Career Center Dashboard",
      description: "Track student engagement, application success rates, and placement outcomes in real-time.",
    },
    {
      icon: Award,
      title: "White-Label Options",
      description: "Brand the platform with your university colors, logo, and domain for a seamless experience.",
    },
    {
      icon: BookOpen,
      title: "Career Portal Integration",
      description: "Seamlessly integrate with your existing Handshake, Symplicity, or custom career portal.",
    },
  ];

  const benefits = [
    "Improve student placement rates",
    "Increase alumni engagement with career services",
    "Reduce time-to-employment for graduates",
    "Provide students with enterprise-grade tools",
    "Track ROI and demonstrate career center value",
    "Custom training sessions for career advisors",
    "Dedicated support team for your institution",
    "FERPA-compliant data handling and security",
  ];


  return (
    <>
      <Helmet>
        <title>Universities & Career Centers | LoopBaan - Student Career Success Platform</title>
        <meta
          name="description"
          content="Empower your students with LoopBaan's enterprise career platform. Bulk accounts, career center dashboards, white-label options, and seamless portal integration."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <GraduationCap className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Universities & Career Centers</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Empower Every Student
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                To Land Their Dream Job
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Give your students and alumni the same AI-powered career tools used by Fortune 500 professionals. Track outcomes, prove ROI, and increase placement rates.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Schedule University Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Education Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Bulk</div>
              <div className="text-gray-400 text-sm md:text-base">Account Management</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Analytics</div>
              <div className="text-gray-400 text-sm md:text-base">Career Center Dashboard</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Custom</div>
              <div className="text-gray-400 text-sm md:text-base">White-Label Branding</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Integration</div>
              <div className="text-gray-400 text-sm md:text-base">Portal Integration</div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Built For Higher Education
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything your career center needs to serve more students, better
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-lg">
                    <feature.icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Universities Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <GraduationCap className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Student Outcomes?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Give your students the career advantage they deserve with enterprise-grade tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
